// The audit log's Export: one CSV row per entry, as the screen shows it.
//
// The rows are the entries the screen holds for the chosen window after its
// filters: `adminAudit` walks the server's keyset cursor to the end of the
// window (and refuses rather than stop early), so what the screen holds is the
// whole window, and the export is the whole filtered window. Quoting and the
// spreadsheet-formula guard are `@/lib/csv`'s.

import type { AuditEvent } from '@/api/types';
import { isoUtc } from '@/lib/datetime';

/** Audit target names recorded before the API (then a Worker) and batch-worker became one SAM
 * Server, keyed by target kind so a user-named object keeps its own name. The
 * stored rows stay as written; this is how they read. */
const RETIRED_TARGET_NAMES: ReadonlyMap<string, string> = new Map([
  ['config\u0000Batch worker config', 'SAM Server config'],
  ['alert_rule\u0000Batch worker heartbeat', 'SAM Server heartbeat'],
]);
/** The heartbeat rename applies to the protected rule only, never a custom rule a
 * person happened to give the same name. */
const HEARTBEAT_RULE_ID = 'system-batch-heartbeat';

/** An audit event's target name as shown on screen, in search and in the CSV. */
export function auditTargetName(e: Pick<AuditEvent, 'target'>): string {
  if (e.target.kind === 'alert_rule' && e.target.id !== HEARTBEAT_RULE_ID) return e.target.name;
  return RETIRED_TARGET_NAMES.get(`${e.target.kind}\u0000${e.target.name}`) ?? e.target.name;
}

/**
 * The credential an entry came through, as the chip reads it: "via API key ‘Nightly
 * export’", or "via Claude" / "via ChatGPT" (the app's name) for a connection a person signed in
 * to from that app. Empty for
 * an entry made in the browser, by a queued job or on a schedule.
 */
export function auditViaLabel(e: Pick<AuditEvent, 'via'>): string {
  if (!e.via) return '';
  return e.via.kind === 'api_key' ? `via API key ‘${e.via.name}’` : `via ${e.via.name || 'a connected app'}`;
}

/** Every change in the entry, `field: before → after`, the inline row's form. */
export function auditDetails(event: Pick<AuditEvent, 'diff'>): string {
  if (!event.diff || event.diff.length === 0) return '';
  return event.diff.map((d) => `${d.field}: ${d.before} → ${d.after}`).join('; ');
}

/**
 * One record per entry, in the screen's order, for `downloadCsv`. The key
 * order is the column order.
 */
export function auditCsvRows(events: readonly AuditEvent[]): Record<string, string>[] {
  return events.map((e) => ({
    'Time (UTC)': isoUtc(e.ts),
    Actor: e.actor.name,
    'Actor email': e.actor.email,
    Action: e.verb,
    Target: auditTargetName(e),
    Details: auditDetails(e),
    'Action code': e.action,
    'Target type': e.target.kind,
    'Target ID': e.target.id,
    'Actor ID': e.actor.id,
    'Entry ID': e.id,
    IP: e.ip,
    'User agent': e.user_agent,
    Via: auditViaLabel(e),
  }));
}
